"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.runStages = void 0;
const inputs_1 = require("./inputs");
const jsonata_1 = require("./jsonata");
const MISSING = { ok: false, reason: 'missing' };
const VALUE_CONTEXTS = ['all', 'utrv', 'disaggregations'];
// Stored stages are untyped, so read their fields as unknown
const fields = (value) => ((typeof value === 'object' && value) || {});
const isResultStage = (stage) => stage.input.context === 'output';
/** Throws the reason a stored stage can't run */
const toStage = (raw, variables) => {
    const { type, formula, output, input } = fields(raw);
    // An old mathjs stage can still be stored
    if (type !== 'jsonata') {
        throw new Error(`Stage type "${String(type)}" is not supported`);
    }
    if (typeof formula !== 'string' || typeof output !== 'string') {
        throw new Error('Stage needs a formula and an output');
    }
    const { context, variable } = fields(input);
    if (context === 'output') {
        return raw;
    }
    if (!VALUE_CONTEXTS.includes(context)) {
        throw new Error(`Stage input context "${String(context)}" is not supported`);
    }
    if (typeof variable !== 'string') {
        throw new Error('Stage needs an input variable');
    }
    if (!Object.hasOwn(variables, variable) || !variables[variable]) {
        throw new Error(`Stage variable "${variable}" is not defined`);
    }
    return raw;
};
const describe = (value) => {
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return 'a list';
    }
    return typeof value === 'string' ? 'text' : `a value of type ${typeof value}`;
};
const round = (value, decimals) => 
// toFixed takes 0 to 100 digits
decimals !== undefined && Number.isInteger(decimals) && decimals >= 0 && decimals <= 100
    ? Number(value.toFixed(decimals))
    : value;
/** A value stage gives a finite number, numeric text included as cells are text; the result stage may give text */
const toStageValue = (value, stage) => {
    if ((0, inputs_1.isMissing)(value)) {
        return undefined;
    }
    const isResult = isResultStage(stage);
    if (isResult && typeof value === 'string') {
        return value;
    }
    const number = typeof value === 'string' ? Number(value) : value;
    if (typeof number !== 'number' || !Number.isFinite(number)) {
        throw new Error(`${isResult ? 'Result' : 'Value'} stage "${stage.output}" gave ${describe(value)}`);
    }
    return isResult ? round(number, stage.decimals) : number;
};
const evaluateStage = async (stage, { variables, utrsData }, results) => {
    if (isResultStage(stage)) {
        return (0, jsonata_1.evaluateJsonata)(stage.formula, results);
    }
    const code = variables[stage.input.variable]?.code;
    const metric = utrsData.find((data) => data.utr.code === code);
    return metric ? (0, jsonata_1.evaluateJsonata)(stage.formula, (0, inputs_1.prepareInputs)(stage.input, metric), { results }) : undefined;
};
/** Runs the value stages in order, then the first result stage; never rejects */
const runStages = async (params) => {
    const { stages = [], variables } = params;
    let current;
    try {
        const checked = [];
        for (const raw of Array.isArray(stages) ? stages : []) {
            current = raw;
            checked.push(toStage(raw, variables));
        }
        current = undefined;
        const resultStage = checked.find(isResultStage);
        if (!resultStage) {
            throw new Error('Stages have no result stage');
        }
        // Null prototype, so any output name is just a key
        const results = Object.assign(Object.create(null), params.results);
        for (const stage of [...checked.slice(0, checked.indexOf(resultStage)), resultStage]) {
            current = stage;
            const value = toStageValue(await evaluateStage(stage, params, results), stage);
            // No data for a value stage means no result, as on dashboards, and isn't an error
            if (value === undefined) {
                return MISSING;
            }
            results[stage.output] = value;
        }
        return { ok: true, value: results[resultStage.output], results };
    }
    catch (e) {
        return { ok: false, reason: 'error', error: e instanceof Error ? e : new Error(String(e)), stage: current };
    }
};
exports.runStages = runStages;
